// Marquette University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Marquette's Common Data Set 2025-2026 (cds-2025-2026_final.pdf), linked
//    from the Office of Institutional Research and Analysis Common Data Set
//    page. The PDF's text layer does not extract, so it was read from page
//    images (pdftoppm). C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans (no
//    early decision; early action with no counts); G1/G5 2026-2027 costs,
//    marked final (the "not available" box is unchecked).
//  - marquette.edu: Apply (first-year deadlines, required and optional
//    materials, the Honors Program early action note), First-Year Student
//    Application Information (the timeline, late Regular Decision review)
//    and Test-Optional Admissions. The deadline pages carry no year; they
//    are the pages as posted for the application that opened in August 2026.
//  - College Scorecard, school id 239105 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Marquette Common Data Set 2025-2026',
        url: 'https://www.marquette.edu/academic-effectiveness/institutional-research-analysis/documents/cds-2025-2026_final.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Marquette Undergraduate Admissions, First-year Students Apply',
        url: 'https://www.marquette.edu/apply/apply.php',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Marquette Undergraduate Admissions, First-Year Student Application Information',
        url: 'https://www.marquette.edu/admissions/undergraduate/first-year-application.php',
        read: '2026-10-01',
    },
    tests: {
        label: 'Marquette Undergraduate Admissions, Test-Optional Admissions',
        url: 'https://www.marquette.edu/apply/test-optional.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?239105-Marquette-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'marquette-university',
    kbId: 'marquette_university',
    name: 'Marquette University',
    shortName: 'Marquette',
    location: 'Milwaukee, WI',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Marquette brand palette: Marquette Blue, Marquette Gold); decoration only.
    branding: {
        primary: '#003366',
        secondary: '#FFCC00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/marquette_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 20002,
        admitted: 15606,
        enrolled: 1913,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4282, admitted: 3302 },
            { group: 'Out-of-state', applied: 14157, admitted: 11282 },
            { group: 'International', applied: 1563, admitted: 1022 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Marquette\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Marquette has no early decision plan. Its early action plan is nonbinding and not restrictive, and the Common Data Set gives no early action counts, so there is no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply with the Marquette application or the Common Application; Marquette has no preference between them. One program has its own timing:',
        rows: [
            { school: 'Honors Program', requirement: 'Students interested in the Honors Program should apply Early Action, by November 15.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 15', decision: 'Late December', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By March 1', binding: false },
        ],
        note: 'Applications completed after February 1 are reviewed on a rolling, space-available basis, with decisions starting in March after on-time Regular Decision applicants hear. Decisions are posted on your application status page.',
        source: 'firstYear',
        binding: 'Early Action is not binding.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'ACT and SAT scores are optional. If you send a score, it is used in Marquette\'s holistic review; if you do not, you are not penalized, and every applicant gets equal and full consideration for admission and scholarships, including the Père Marquette Scholarship.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 53890 },
            { item: 'Required fees', amount: 1200 },
            { item: 'Food and housing (on campus)', amount: 17480 },
            { item: 'Books and supplies', amount: 720 },
            { item: 'Transportation', amount: 582 },
            { item: 'Other expenses', amount: 2950 },
        ],
        total: 76822,
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 30561,
            byIncome: [
                { income: '$0 to $30,000', amount: 20708 },
                { income: '$30,001 to $48,000', amount: 20217 },
                { income: '$48,001 to $75,000', amount: 23377 },
                { income: '$75,001 to $110,000', amount: 30314 },
                { income: 'Over $110,000', amount: 37187 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Marquette\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
